import { useState, useMemo, useEffect } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useAuth } from "@/contexts/AuthContext";
import { validateReceiptVoucher } from "@/lib/accounting/engine";
import { VoucherLine, InvoiceAllocation, Voucher } from "@/lib/accounting/types";
import { PartyAutocomplete } from "@/components/accounting/PartyAutocomplete";
import { Button } from "@/components/ui/button";
import { FormModal } from "@/components/form/FormModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { IndianRupee, Landmark, Wallet, CheckCircle2, FileText, Smartphone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ReceiptVoucherFormProps {
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  preselectedPartyId?: string;
  preselectedInvoice?: Voucher;
}

export function ReceiptVoucherForm({
  trigger,
  onSuccess,
  isOpen,
  onOpenChange,
  preselectedPartyId,
  preselectedInvoice,
}: ReceiptVoucherFormProps) {
  const { ledgers, vouchers, createVoucher } = useAccounting();
  const { role } = useAuth();
  const { toast } = useToast();

  const [internalOpen, setInternalOpen] = useState(false);
  const open = isOpen !== undefined ? isOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const [date, setDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [payerType, setPayerType] = useState<"party" | "income">("party");
  const [partyLedgerId, setPartyLedgerId] = useState(preselectedPartyId || "");
  const [incomeLedgerId, setIncomeLedgerId] = useState("");
  const [receivingLedgerId, setReceivingLedgerId] = useState("");
  const [paymentMode, setPaymentMode] = useState<string>("UPI");
  const [amount, setAmount] = useState<string>(preselectedInvoice ? String(preselectedInvoice.totalDebit) : "");
  const [instrumentNo, setInstrumentNo] = useState("");
  const [instrumentDate, setInstrumentDate] = useState("");
  const [bankName, setBankName] = useState("");
  const [narration, setNarration] = useState("");
  const [selectedInvoices, setSelectedInvoices] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);

  // Filter Cash and Bank Ledgers for receiving destination
  const cashBankLedgers = useMemo(
    () => ledgers.filter((l) => l.group === "Cash-in-Hand" || l.group === "Bank Accounts"),
    [ledgers]
  );

  // Filter Income / Revenue ledgers
  const incomeLedgers = useMemo(
    () =>
      ledgers.filter((l) =>
        ["Sales Accounts", "Direct Income", "Indirect Income", "Revenue"].includes(l.group)
      ),
    [ledgers]
  );

  // Default receiving ledger
  useEffect(() => {
    if (!receivingLedgerId && cashBankLedgers.length > 0) {
      const defaultBank = cashBankLedgers.find((l) => l.group === "Bank Accounts") || cashBankLedgers[0];
      setReceivingLedgerId(defaultBank.id);
    }
  }, [cashBankLedgers, receivingLedgerId]);

  // Find open sales invoices for this party to allocate receipt against
  const openInvoices = useMemo(() => {
    if (!partyLedgerId) return [];
    return vouchers.filter(
      (v) =>
        v.type === "Sales" &&
        v.status === "Approved" &&
        (v.partyLedgerId === partyLedgerId || v.partyName?.toLowerCase() === ledgers.find((l) => l.id === partyLedgerId)?.name?.toLowerCase()) &&
        (v.paymentStatus !== "Paid" || (v.paidAmount || 0) < v.totalDebit)
    );
  }, [vouchers, partyLedgerId, ledgers]);

  // Pre-select invoice if passed in props
  useEffect(() => {
    if (preselectedInvoice && preselectedInvoice.partyLedgerId) {
      setPartyLedgerId(preselectedInvoice.partyLedgerId);
      const remaining = preselectedInvoice.totalDebit - (preselectedInvoice.paidAmount || 0);
      setAmount(String(remaining > 0 ? remaining : preselectedInvoice.totalDebit));
      setSelectedInvoices({ [preselectedInvoice.id]: remaining > 0 ? remaining : preselectedInvoice.totalDebit });
    }
  }, [preselectedInvoice]);

  const numAmount = parseFloat(amount) || 0;
  const receivingLedger = useMemo(() => cashBankLedgers.find((l) => l.id === receivingLedgerId), [cashBankLedgers, receivingLedgerId]);
  const partyLedger = useMemo(() => ledgers.find((l) => l.id === partyLedgerId), [ledgers, partyLedgerId]);
  const incomeLedger = useMemo(() => ledgers.find((l) => l.id === incomeLedgerId), [ledgers, incomeLedgerId]);

  const creditLedger = payerType === "party" ? partyLedger : incomeLedger;

  const handleAllocateInvoice = (invId: string, invAmount: number, checked: boolean) => {
    setSelectedInvoices((prev) => {
      const updated = { ...prev };
      if (checked) {
        updated[invId] = invAmount;
      } else {
        delete updated[invId];
      }
      // Auto-sum selected invoices into total amount
      const sum = Object.values(updated).reduce((s, a) => s + a, 0);
      if (sum > 0) setAmount(String(sum));
      return updated;
    });
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!receivingLedger) {
      toast({ title: "Account Required", description: "Please select the Cash or Bank account where funds were received.", variant: "destructive" });
      return;
    }
    if (!creditLedger) {
      toast({ title: "Payer Required", description: "Please select the party or income account from which money was received.", variant: "destructive" });
      return;
    }
    if (numAmount <= 0) {
      toast({ title: "Invalid Amount", description: "Receipt amount must be greater than zero.", variant: "destructive" });
      return;
    }

    const validation = validateReceiptVoucher(receivingLedger.group, numAmount);
    if (!validation.valid) {
      toast({ title: "Receipt Rule Violation", description: validation.error, variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      // In double-entry accounting:
      // Debit: Cash or Bank account (Money received into the business)
      // Credit: Customer / Debtor / Income (Receivable settled / income recognized)
      const lines: VoucherLine[] = [
        {
          id: crypto.randomUUID(),
          ledgerId: receivingLedger.id,
          ledgerName: receivingLedger.name,
          debit: numAmount,
          credit: 0,
          narration: `Funds received into ${receivingLedger.name} via ${paymentMode}`,
        },
        {
          id: crypto.randomUUID(),
          ledgerId: creditLedger.id,
          ledgerName: creditLedger.name,
          debit: 0,
          credit: numAmount,
          narration: `Receipt from ${creditLedger.name}`,
        },
      ];

      // Build allocations list
      const allocations: InvoiceAllocation[] = Object.entries(selectedInvoices).map(([invId, allocAmt]) => {
        const inv = vouchers.find((v) => v.id === invId);
        return {
          invoiceId: invId,
          invoiceNumber: inv?.invoiceNumber || inv?.voucherNumber || invId,
          allocatedAmount: allocAmt,
        };
      });

      const defaultNarration = `Received ₹${numAmount.toLocaleString("en-IN")} from ${creditLedger.name} via ${paymentMode}${
        instrumentNo ? ` | Ref: ${instrumentNo}` : ""
      }`;

      const finalNarration = narration.trim() || defaultNarration;

      const result = await createVoucher(
        "Receipt",
        lines,
        finalNarration,
        payerType === "party" ? creditLedger.id : undefined,
        undefined,
        0, 0, 0, 0,
        undefined,
        {
          date,
          paymentMode,
          instrumentNo: instrumentNo.trim() || undefined,
          instrumentDate: instrumentDate || undefined,
          bankName: bankName.trim() || receivingLedger.name,
          bankLedgerId: receivingLedger.id,
          allocations: allocations.length > 0 ? allocations : undefined,
        }
      );

      if (result.success && result.voucher) {
        toast({
          title: "Receipt Recorded ✓",
          description: `${result.voucher.voucherNumber}: ₹${numAmount.toLocaleString("en-IN")} received from ${creditLedger.name}.`,
        });
        setOpen(false);
        setAmount("");
        setInstrumentNo("");
        setNarration("");
        setSelectedInvoices({});
        onSuccess?.();
      } else {
        toast({ title: "Receipt Failed", description: result.error || "Unable to save receipt voucher.", variant: "destructive" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={open}
      onOpenChange={setOpen}
      size="transaction"
      trigger={
        trigger ? (
          trigger
        ) : isOpen === undefined ? (
          <Button variant="outline" className="gap-2">
            <IndianRupee className="h-4 w-4" />
            <span>Receive Money (F6)</span>
          </Button>
        ) : undefined
      }
      icon={<IndianRupee className="h-5 w-5 text-emerald-600" />}
      title="Record Receipt Voucher (F6)"
      description="Record customer collections, sales invoice payments, revenue receipts, and refunds into your bank or cash account."
      footerLeft={
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(false)}
          disabled={submitting}
          className="text-xs h-9"
        >
          Cancel <span className="text-[10px] text-muted-foreground ml-1">Esc</span>
        </Button>
      }
      footerRight={
        <Button
          type="submit"
          form="receipt-voucher-form"
          disabled={submitting}
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9 font-semibold gap-1.5 shadow-sm"
        >
          {submitting ? "Saving Receipt..." : "Record Receipt Voucher"}
        </Button>
      }
    >
      <form id="receipt-voucher-form" onSubmit={handleSubmit} className="space-y-4">
          {/* Payer Type */}
          <div className="flex items-center gap-4 border-b border-border pb-2 text-xs">
            <label className="font-semibold text-muted-foreground uppercase tracking-wider">Source Type:</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="payerType"
                  checked={payerType === "party"}
                  onChange={() => setPayerType("party")}
                  className="text-primary"
                />
                <span>Party (Customer / Client)</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="payerType"
                  checked={payerType === "income"}
                  onChange={() => setPayerType("income")}
                  className="text-primary"
                />
                <span>Direct Revenue / Income Ledger</span>
              </label>
            </div>
          </div>

          {/* Received From & Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                {payerType === "party" ? "Received From (Customer / Client) *" : "Income Ledger *"}
              </label>
              {payerType === "party" ? (
                <PartyAutocomplete
                  value={partyLedgerId}
                  onSelect={(id) => setPartyLedgerId(id)}
                  partyType="customer"
                />
              ) : (
                <Select value={incomeLedgerId} onValueChange={setIncomeLedgerId}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="Select income ledger..." />
                  </SelectTrigger>
                  <SelectContent>
                    {incomeLedgers.map((l) => (
                      <SelectItem key={l.id} value={l.id} className="text-xs">
                        {l.name} ({l.group})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1 flex items-center justify-between">
                <span>Receipt Date *</span>
                {date !== new Date().toISOString().split("T")[0] && (
                  <span className="text-[10px] text-amber-500 font-semibold">Backdated</span>
                )}
              </label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-9 text-xs font-mono"
                required
              />
            </div>
          </div>

          {/* Receiving Account & Mode */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Received Into (Cash / Bank Account) *
              </label>
              <Select value={receivingLedgerId} onValueChange={setReceivingLedgerId}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Select receiving account..." />
                </SelectTrigger>
                <SelectContent>
                  {cashBankLedgers.map((l) => (
                    <SelectItem key={l.id} value={l.id} className="text-xs">
                      {l.name} ({l.group}) · Bal: ₹{l.currentBalance.toLocaleString("en-IN")} {l.currentBalanceType}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Payment Mode *
              </label>
              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Select mode..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UPI">UPI (GPay / PhonePe / Paytm / BHIM)</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer (NEFT / RTGS / IMPS)</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Debit/Credit Card">Debit / Credit Card</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Amount & Reference Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Amount Received (₹) *
              </label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-9 text-xs font-mono font-semibold"
                required
              />
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                {paymentMode === "Cheque"
                  ? "Cheque Number"
                  : paymentMode === "UPI"
                  ? "UPI Transaction Ref / UTR"
                  : "UTR / Transaction Reference"}
              </label>
              <Input
                type="text"
                placeholder="e.g. UPI-928103 or CHQ-40291"
                value={instrumentNo}
                onChange={(e) => setInstrumentNo(e.target.value)}
                className="h-9 text-xs font-mono"
              />
            </div>
          </div>

          {/* Instrument Date & Bank Name */}
          {(paymentMode === "Cheque" || paymentMode === "Bank Transfer") && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                  Instrument / Cheque Date
                </label>
                <Input
                  type="date"
                  value={instrumentDate}
                  onChange={(e) => setInstrumentDate(e.target.value)}
                  className="h-9 text-xs font-mono"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                  Drawer Bank Name
                </label>
                <Input
                  type="text"
                  placeholder="e.g. State Bank of India"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="h-9 text-xs"
                />
              </div>
            </div>
          )}

          {/* Outstanding Invoice Allocation Section */}
          {openInvoices.length > 0 && (
            <div className="p-3 rounded-lg border border-border bg-muted/20 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-foreground flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-success" />
                  Allocate Receipt Against Open Sales Invoices ({openInvoices.length})
                </span>
                <span className="text-[11px] text-muted-foreground">Check to settle invoice</span>
              </div>
              <div className="space-y-1.5 max-h-36 overflow-y-auto">
                {openInvoices.map((inv) => {
                  const remaining = inv.totalDebit - (inv.paidAmount || 0);
                  const isChecked = selectedInvoices[inv.id] !== undefined;
                  return (
                    <div
                      key={inv.id}
                      className="flex items-center justify-between p-2 rounded bg-card border border-border text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => handleAllocateInvoice(inv.id, remaining, e.target.checked)}
                          className="rounded text-primary"
                        />
                        <div>
                          <span className="font-medium text-foreground">{inv.invoiceNumber || inv.voucherNumber}</span>
                          <span className="text-muted-foreground ml-2 text-[11px]">({inv.date})</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="font-mono font-semibold text-foreground">
                          ₹{remaining.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </span>
                        {inv.paidAmount ? (
                          <span className="text-[10px] text-muted-foreground block">
                            (Paid: ₹{inv.paidAmount.toLocaleString("en-IN")})
                          </span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Narration */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
              Narration / Notes
            </label>
            <Input
              type="text"
              placeholder="e.g. Received payment for services rendered"
              value={narration}
              onChange={(e) => setNarration(e.target.value)}
              className="h-9 text-xs"
            />
          </div>

          {/* Double-Entry Preview */}
          {creditLedger && receivingLedger && numAmount > 0 && (
            <div className="p-2.5 rounded-lg bg-muted/40 border border-border text-xs space-y-1">
              <span className="font-semibold text-muted-foreground uppercase tracking-wider text-[10px]">
                Double-Entry Posting Preview:
              </span>
              <div className="flex justify-between items-center text-foreground font-mono">
                <span>Dr. {receivingLedger.name} (Deposit)</span>
                <span>₹{numAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center text-muted-foreground font-mono pl-4">
                <span>Cr. {creditLedger.name} (Settlement)</span>
                <span>₹{numAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          )}

      </form>
    </FormModal>
  );
}
